export type EmbeddedSignupEvent = {
  type?: string;
  data?: Record<string, unknown>;
};

/**
 * Coexistence on Embedded Signup v4.
 * `version` is the flow version. `sessionInfoVersion` is the session-event
 * payload format; Meta's only documented value is "3", including when the
 * Embedded Signup Builder emits a v4 configuration.
 */
export const embeddedSignupLoginExtras = {
  setup: {},
  featureType: "whatsapp_business_app_onboarding",
  sessionInfoVersion: "3",
  version: "v4",
} as const;

export type EmbeddedSignupSaveResult = {
  updated: any;
  event: EmbeddedSignupEvent | null;
};

/**
 * Meta usually posts WA_EMBEDDED_SIGNUP before FB.login hands back the OAuth
 * code, so this normally returns on the first poll. The cap is short because
 * the code is not sent until it resolves: until then the only copy lives in
 * the browser tab, and closing the panel loses the exchange entirely.
 */
export const EMBEDDED_SIGNUP_EVENT_TIMEOUT_MS = 1500;

function asSignupEvent(value: unknown): EmbeddedSignupEvent | null {
  const event = value as EmbeddedSignupEvent | undefined | null;
  return event?.type === "WA_EMBEDDED_SIGNUP" ? event : null;
}

export async function waitForEmbeddedSignupMessage(
  getLatest: () => unknown,
  timeoutMs = EMBEDDED_SIGNUP_EVENT_TIMEOUT_MS,
  pollMs = 50,
): Promise<EmbeddedSignupEvent | null> {
  const start = Date.now();
  for (;;) {
    const event = asSignupEvent(getLatest());
    if (event) return event;
    if (Date.now() - start >= timeoutMs) return null;
    await new Promise((r) => setTimeout(r, pollMs));
  }
}

export function embeddedSignupIdFields(
  event: EmbeddedSignupEvent | null,
): Record<string, unknown> {
  const data = event?.data || {};
  const fields: Record<string, unknown> = {};
  if (data.phone_number_id) fields.wapp_phone_number_id = data.phone_number_id;
  if (data.waba_id) fields.wapp_waba_id = data.waba_id;
  if (data.business_id) fields.wapp_business_id = data.business_id;
  return fields;
}

/**
 * Send the code and Meta's ids in a single PATCH. They have to travel together:
 * the backend only runs `subscribe_waba`, the display-phone sync and template
 * setup on the request carrying the code, so ids that arrive afterwards would
 * never be applied to that setup.
 */
export async function saveEmbeddedSignupCode({
  code,
  getLatestEvent,
  patch,
  timeoutMs = EMBEDDED_SIGNUP_EVENT_TIMEOUT_MS,
  pollMs = 50,
}: {
  code: string;
  getLatestEvent: () => unknown;
  patch: (payload: Record<string, unknown>) => Promise<any>;
  timeoutMs?: number;
  pollMs?: number;
}): Promise<EmbeddedSignupSaveResult> {
  const event = await waitForEmbeddedSignupMessage(
    getLatestEvent,
    timeoutMs,
    pollMs,
  );

  const payload: Record<string, unknown> = {
    wapp_embedded_signup_code: code,
    ...embeddedSignupIdFields(event),
  };
  // Omit the key rather than sending null, which would blank a stored event.
  if (event) payload.wapp_embedded_signup_event = event;

  const updated = await patch(payload);
  return { updated, event };
}
